import { useCallback, useEffect, useRef, useState } from 'react';

type UseDemoTimerOptions = {
  durationSeconds: number;
  isRunning: boolean;
  onExpired?: () => void;
};

type UseDemoTimerReturn = {
  remainingSeconds: number;
  formattedTime: string;
  isExpired: boolean;
};

export function useDemoTimer({
  durationSeconds,
  isRunning,
  onExpired,
}: UseDemoTimerOptions): UseDemoTimerReturn {
  const [remainingSeconds, setRemainingSeconds] = useState(durationSeconds);
  const [isExpired, setIsExpired] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onExpiredRef = useRef(onExpired);

  useEffect(() => {
    onExpiredRef.current = onExpired;
  }, [onExpired]);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Reset timer when isRunning transitions to true (new demo session)
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (isRunning && !wasRunningRef.current) {
      setRemainingSeconds(durationSeconds);
      setIsExpired(false);
    }
    wasRunningRef.current = isRunning;
  }, [isRunning, durationSeconds]);

  useEffect(() => {
    if (!isRunning || isExpired) {
      clearTimer();
      return;
    }

    intervalRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          clearTimer();
          setIsExpired(true);
          onExpiredRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return clearTimer;
  }, [isRunning, isExpired, clearTimer]);

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return {
    remainingSeconds,
    formattedTime,
    isExpired,
  };
}
